import type { ReactNode } from 'react';
import { Menu } from 'lucide-react';
import { Link } from 'react-router';
import { cx } from '../ui/cx';

/**
 * Top navbar (Ace .navbar, skin-3 #404040). A <header> banner landmark. `on-dark` switches the
 * focus ring to the light variant. The menu button toggles the sidebar drawer below `lg`.
 */
export function AppHeader({
  brand = 'MantisBT',
  brandTo = '/',
  onMenuClick,
  menuOpen,
  menuControls,
  menuLabel = 'Main menu',
  children,
  end,
  className,
}: {
  brand?: ReactNode;
  brandTo?: string;
  onMenuClick?: () => void;
  menuOpen?: boolean;
  /** id of the element the menu button controls (the sidebar drawer). */
  menuControls?: string;
  menuLabel?: string;
  /** Middle slot (e.g. project selector). */
  children?: ReactNode;
  /** Right slot (e.g. search, user menu). */
  end?: ReactNode;
  className?: string;
}) {
  return (
    <header
      className={cx(
        'on-dark sticky top-0 z-(--z-navbar) flex h-navbar items-center gap-2 bg-navbar px-2 text-ink-inverse sm:px-3',
        className,
      )}
    >
      {onMenuClick && (
        <button
          type="button"
          onClick={onMenuClick}
          aria-expanded={menuOpen}
          aria-controls={menuControls}
          aria-label={menuLabel}
          className="flex size-10 items-center justify-center hover:bg-navbar-hover lg:hidden"
        >
          <Menu aria-hidden="true" className="size-5" />
        </button>
      )}
      <Link
        to={brandTo}
        className="flex items-center gap-2 px-1 text-xl font-light text-ink-inverse no-underline hover:text-ink-inverse hover:no-underline sm:text-3xl"
      >
        {brand}
      </Link>
      <div className="flex min-w-0 flex-1 items-center gap-2">{children}</div>
      {end && <div className="flex items-center gap-1">{end}</div>}
    </header>
  );
}
